import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import fg from '@atlaskit/feature-gate-js-client';
import MoreIcon from '@atlaskit/icon/glyph/more';

import type { FlagsStateContainer } from '@confluence/flags';
import { useStringFeatureFlag } from '@confluence/session-data';

import { MACRO_FALLBACK_UI_FF } from '../constants';

const i18n = defineMessages({
	fallbackFlagTitle: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.title',
		defaultMessage: 'This feature isn’t supported yet',
		description: 'The title of the fallback flag when an unsupported macro is added',
	},
	fallbackFlagDescription: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.description',
		defaultMessage:
			'The macro you’ve selected is not yet part of the Early Access Program. Publish via the <menuIcon></menuIcon> menu on the toolbar to display it properly.',
		description: 'The body of the fallback flag when an unsupported macro is added',
	},
	fallbackFlagLearnMoreAction: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.action',
		defaultMessage: 'Learn more about this EAP',
		description: 'The link to learn more on the fallback flag when an unsupported macro is added',
	},
});

export const useMacroFallbackFlag = (flags: FlagsStateContainer, isLivePage: Boolean) => {
	const macrosToShowFallbackUi = useStringFeatureFlag(MACRO_FALLBACK_UI_FF);
	const intl = useIntl();

	const showMacroFallbackFlag = useCallback(
		async (macro: string | undefined) => {
			const allowAll = macrosToShowFallbackUi === 'ALLOW_ALL';
			if (!macrosToShowFallbackUi || !macro || !isLivePage || allowAll) {
				return;
			}

			const macros = macrosToShowFallbackUi.split(',').map((m) => m.trim().toLowerCase());
			const macroKey = macro.toLowerCase();

			const isCreateFromTemplateMacroAllowed =
				macroKey === 'create-from-template' &&
				fg.checkGate('confluence_frontend_page_experiences_cft_wysiwig');

			const isLiveSearchMacroAllowed =
				macroKey === 'livesearch' && fg.checkGate('cc_page_experiences_live_search_wysiwyg');

			const isAnchroMacroRefreshAllowed =
				macroKey === 'anchor' && fg.checkGate('cc_page_experiences_anchor_macro_refresh');

			// If macro is in the allowlist, or allowed barring a special case, don't show the warning
			if (
				macros.includes(macroKey) ||
				isCreateFromTemplateMacroAllowed ||
				isLiveSearchMacroAllowed ||
				isAnchroMacroRefreshAllowed
			) {
				return;
			}

			await flags.showFlag({
				type: 'warning',
				close: 'auto',
				title: intl.formatMessage(i18n.fallbackFlagTitle),
				description: intl.formatMessage(i18n.fallbackFlagDescription, {
					menuIcon: () => <MoreIcon size="small" label="Icon for publish menu" />,
				}),
				// TODO: Change this to the correct link when available
				// actions: [
				//   {
				//     content: intl.formatMessage(i18n.fallbackFlagLearnMoreAction),
				//     href: "#",
				//   },
				// ],
			});
		},
		[flags, macrosToShowFallbackUi, intl, isLivePage],
	);

	return { showMacroFallbackFlag };
};
